// =============================================================================
// planner.ts — Senior Year Planner milestone derivation (v0.2.34, Wave A)
// =============================================================================
//
// The planner does not store milestones. It DERIVES them, live, from the
// applicant's scholarships: each scholarship with a deadline and a given
// requirement produces prep milestones counted back from that deadline by the
// household's offset rules.
//
// Deriving (rather than persisting) keeps Wave A simple and correct: there is
// no stale data and no regeneration problem — when a deadline or requirement
// changes, the milestones recompute on the next render. Persisted milestone
// status / manual overrides are Wave B.
// =============================================================================

import type { Scholarship, TargetSchool, PlannerOffsets } from '@scholarship-app/types';
import { normalizeToIso } from './dates';

/** Task categories — each gets one color in the UI. */
export type MilestoneCategory =
  | 'essay'
  | 'recommendation'
  | 'transcript_scores'
  | 'portfolio'
  | 'submit'
  | 'admission';

export const CATEGORY_LABELS: Record<MilestoneCategory, string> = {
  essay: 'Essays',
  recommendation: 'Recommendations',
  transcript_scores: 'Transcripts & scores',
  portfolio: 'Portfolio',
  submit: 'Final submission',
  admission: 'College applications',
};

/**
 * One derived prep milestone. As of v0.2.37 (Wave B2) a milestone derives
 * from either a scholarship deadline or a target-school admission deadline;
 * `sourceType` distinguishes them. For admission milestones, `scholarshipId`
 * carries the target school's id and `scholarshipName` its name (the field
 * names are kept for compatibility with the existing UI/persistence).
 */
export interface DerivedMilestone {
  id: string;                    // stable per (source, type)
  sourceType: 'scholarship' | 'admission';
  scholarshipId: string;         // scholarship_id OR (for admission) school_id
  scholarshipName: string;       // scholarship name OR school name
  category: MilestoneCategory;
  title: string;
  targetDate: string;            // ISO yyyy-mm-dd
  deadlineDate: string;          // the deadline it derives from
  daysBeforeDeadline: number;
  isOverdue: boolean;
  isFinalSubmit: boolean;
}

/** Sensible default offsets — used to seed the settings doc and as a fallback. */
export const DEFAULT_PLANNER_OFFSETS: Omit<PlannerOffsets, 'updated_at' | 'updated_by'> = {
  essay_draft_days_before: 30,
  essay_final_days_before: 10,
  recommendation_request_days_before: 45,
  transcript_request_days_before: 21,
  test_scores_send_days_before: 21,
  portfolio_days_before: 14,
};

/** Subtract `days` from an ISO date, returning ISO yyyy-mm-dd. */
function minusDays(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function isPast(iso: string): boolean {
  return iso < new Date().toISOString().slice(0, 10);
}

/**
 * Derive the prep milestones for a single scholarship. Only produces
 * milestones the scholarship's requirements actually call for.
 */
export function deriveScholarshipMilestones(
  s: Scholarship,
  offsets: Pick<PlannerOffsets,
    'essay_draft_days_before' | 'essay_final_days_before' |
    'recommendation_request_days_before' | 'transcript_request_days_before' |
    'test_scores_send_days_before' | 'portfolio_days_before'>
): DerivedMilestone[] {
  const deadlineRaw = s.effective_deadline ?? s.deadline;
  if (!deadlineRaw) return [];
  // Submitted/awarded/rejected/expired scholarships need no prep timeline.
  if (s.status === 'submitted' || s.status === 'awarded' ||
      s.status === 'rejected' || s.status === 'expired') {
    return [];
  }
  // The deadline string is not guaranteed to be a clean ISO date —
  // scholarships promoted before v0.2.20.1, or captured with odd date
  // formats, can carry unparseable values. normalizeToIso returns a clean
  // yyyy-MM-dd or null; if it can't be parsed, this scholarship simply
  // produces no milestones (same as one with no deadline) rather than
  // generating a broken date that crashes the planner.
  const deadline = normalizeToIso(deadlineRaw);
  if (!deadline) return [];
  // `deadline` is now definitely a string; bind it so the narrowing carries
  // into the nested `add` closure (TS doesn't propagate it otherwise).
  const deadlineDate: string = deadline;
  const req = s.requirements;
  const out: DerivedMilestone[] = [];

  function add(
    type: string,
    category: MilestoneCategory,
    title: string,
    daysBefore: number,
    isFinalSubmit = false
  ) {
    // A milestone whose computed date is after the deadline (offset larger
    // than the time remaining) is clamped to the deadline itself.
    const target = daysBefore > 0 ? minusDays(deadlineDate, daysBefore) : deadlineDate;
    const clamped = target > deadlineDate ? deadlineDate : target;
    out.push({
      id: `${s.scholarship_id}__${type}`,
      sourceType: 'scholarship',
      scholarshipId: s.scholarship_id,
      scholarshipName: s.name,
      category,
      title,
      targetDate: clamped,
      deadlineDate: deadlineDate,
      daysBeforeDeadline: daysBefore,
      isOverdue: isPast(clamped),
      isFinalSubmit,
    });
  }

  if (req?.essay_required) {
    add('essay_draft', 'essay', 'Draft essay', offsets.essay_draft_days_before);
    add('essay_final', 'essay', 'Finalize essay', offsets.essay_final_days_before);
  }
  if (req && req.letters_of_rec_required > 0) {
    const n = req.letters_of_rec_required;
    add('rec_request', 'recommendation',
      `Request ${n} recommendation${n > 1 ? 's' : ''}`,
      offsets.recommendation_request_days_before);
  }
  if (req?.transcript_required) {
    add('transcript', 'transcript_scores', 'Request transcript',
      offsets.transcript_request_days_before);
  }
  if (req?.test_scores_required) {
    add('test_scores', 'transcript_scores', 'Send test scores',
      offsets.test_scores_send_days_before);
  }
  if (req?.portfolio_required) {
    add('portfolio', 'portfolio', 'Prepare portfolio', offsets.portfolio_days_before);
  }
  // Every scholarship gets a final-submission milestone on the deadline.
  add('submit', 'submit', 'Submit application', 0, true);

  return out;
}

/** Derive milestones across all the applicant's scholarships, sorted by date. */
export function deriveAllMilestones(
  scholarships: Scholarship[],
  offsets: Pick<PlannerOffsets,
    'essay_draft_days_before' | 'essay_final_days_before' |
    'recommendation_request_days_before' | 'transcript_request_days_before' |
    'test_scores_send_days_before' | 'portfolio_days_before'>
): DerivedMilestone[] {
  const all: DerivedMilestone[] = [];
  for (const s of scholarships) {
    all.push(...deriveScholarshipMilestones(s, offsets));
  }
  all.sort((a, b) => a.targetDate.localeCompare(b.targetDate));
  return all;
}

/**
 * Derive admission milestones for a single target school (v0.2.37, Wave B2).
 * Per the confirmed design (Option A): each admission deadline the school has
 * produces ONE "Submit application" milestone on that date — no assumed prep
 * chain. A school can have up to three (early action / early decision /
 * regular decision); most applicants use one path, so generating a prep chain
 * for all three would flood the planner with milestones for unused paths.
 */
export function deriveAdmissionMilestones(school: TargetSchool): DerivedMilestone[] {
  if (school.archived) return [];
  const out: DerivedMilestone[] = [];
  const d = school.admission_deadlines;
  if (!d) return out;
  const label = school.school_short_name || school.school_name;

  const deadlineTypes: { key: keyof typeof d; slug: string; phase: string }[] = [
    { key: 'early_action', slug: 'ea', phase: 'early action' },
    { key: 'early_decision', slug: 'ed', phase: 'early decision' },
    { key: 'regular_decision', slug: 'rd', phase: 'regular decision' },
  ];

  for (const dt of deadlineTypes) {
    const raw = d[dt.key];
    if (!raw || typeof raw !== 'string') continue;
    const iso = normalizeToIso(raw);
    if (!iso) continue;
    out.push({
      id: `${school.school_id}__admission_${dt.slug}`,
      sourceType: 'admission',
      scholarshipId: school.school_id,
      scholarshipName: school.school_name,
      category: 'admission',
      title: `Submit ${label} application (${dt.phase})`,
      targetDate: iso,
      deadlineDate: iso,
      daysBeforeDeadline: 0,
      isOverdue: isPast(iso),
      isFinalSubmit: true,
    });
  }
  return out;
}

/**
 * Derive every milestone for the planner: scholarship prep milestones plus
 * target-school admission milestones (v0.2.37). `targetSchools` is optional
 * so existing callers that pass only scholarships still compile.
 */
export function deriveAllMilestonesWithSchools(
  scholarships: Scholarship[],
  targetSchools: TargetSchool[],
  offsets: Pick<PlannerOffsets,
    'essay_draft_days_before' | 'essay_final_days_before' |
    'recommendation_request_days_before' | 'transcript_request_days_before' |
    'test_scores_send_days_before' | 'portfolio_days_before'>
): DerivedMilestone[] {
  const all: DerivedMilestone[] = [];
  for (const s of scholarships) {
    all.push(...deriveScholarshipMilestones(s, offsets));
  }
  for (const sch of targetSchools) {
    all.push(...deriveAdmissionMilestones(sch));
  }
  all.sort((a, b) => a.targetDate.localeCompare(b.targetDate));
  return all;
}

/** Group milestones by ISO month (yyyy-mm) for the agenda view. */
export function groupByMonth(
  milestones: DerivedMilestone[]
): { month: string; items: DerivedMilestone[] }[] {
  const groups = new Map<string, DerivedMilestone[]>();
  for (const m of milestones) {
    const month = m.targetDate.slice(0, 7);
    if (!groups.has(month)) groups.set(month, []);
    groups.get(month)!.push(m);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, items]) => ({ month, items }));
}

/**
 * View-model for a milestone in the Planner UI (v0.2.35, Wave B1). Combines
 * the derived display fields with the persisted status. `status` of
 * 'overdue' is computed for display (a pending milestone past its date) —
 * it is not a stored status value.
 */
export interface PlannerMilestone extends DerivedMilestone {
  status: 'pending' | 'in_progress' | 'complete';
  isComplete: boolean;
}

/** Group PlannerMilestones by ISO month. */
export function groupPlannerByMonth(
  milestones: PlannerMilestone[]
): { month: string; items: PlannerMilestone[] }[] {
  const groups = new Map<string, PlannerMilestone[]>();
  for (const m of milestones) {
    const month = m.targetDate.slice(0, 7);
    if (!groups.has(month)) groups.set(month, []);
    groups.get(month)!.push(m);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([month, items]) => ({ month, items }));
}
